import { cn } from "@/lib/utils";
import { scoreLevel } from "./score-colors";

/**
 * Cartographic score-visualization primitives — the chip / bar / pill language
 * from the Flotion redesign. Colors ride on the `--score-*` brand tokens, so
 * they stay correct in light and dark.
 */

// scoreLevel() yields "high" | "medium" | "low"; the "medium" level maps to the
// "med" brand token (e.g. --color-score-med / bg-score-med-soft).
const CHIP_STYLES = {
  high: "bg-score-high-soft text-score-high",
  medium: "bg-score-med-soft text-score-med",
  low: "bg-score-low-soft text-score-low",
} as const;

const BAR_FILL = {
  high: "bg-score-high",
  medium: "bg-score-med",
  low: "bg-score-low",
} as const;

/** Pill with a colored dot + tabular score. Used in tables, cards, popups. */
export function ScoreChip({
  score,
  className,
}: {
  score: number | null | undefined;
  className?: string;
}) {
  if (score == null) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 font-mono text-xs font-medium tabular-nums text-muted-foreground",
          className,
        )}
      >
        —
      </span>
    );
  }
  const lvl = scoreLevel(score);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full py-0.5 pl-1.5 pr-2 font-mono text-xs font-medium tabular-nums",
        CHIP_STYLES[lvl],
        className,
      )}
    >
      <span className="size-1.5 rounded-full bg-current opacity-95" aria-hidden="true" />
      {Math.round(score)}
    </span>
  );
}

/** Tabular score number followed by a mini progress bar. */
export function ScoreBar({
  score,
  className,
}: {
  score: number | null | undefined;
  className?: string;
}) {
  if (score == null) return <span className="text-muted-foreground">—</span>;
  const lvl = scoreLevel(score);
  return (
    <span className={cn("inline-flex items-center gap-2 whitespace-nowrap", className)}>
      <span className="min-w-[1.5rem] text-right font-mono text-xs font-medium tabular-nums">
        {Math.round(score)}
      </span>
      <span className="inline-block h-1 w-20 overflow-hidden rounded-full bg-muted align-middle">
        <span
          className={cn("block h-full rounded-full", BAR_FILL[lvl])}
          style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
        />
      </span>
    </span>
  );
}

const RING_COLOR = {
  high: "var(--score-high)",
  medium: "var(--score-med)",
  low: "var(--score-low)",
} as const;

/** Circular score gauge — the FloScore as a ring + centered tabular number. */
export function ScoreRing({
  score,
  size = 64,
}: {
  score: number | null | undefined;
  size?: number;
}) {
  const r = 28;
  const circumference = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.min(Math.max(score, 0), 100);
  const dash = (circumference * pct) / 100;
  const color = score == null ? "var(--score-none)" : RING_COLOR[scoreLevel(score)];
  return (
    <span
      className="relative grid shrink-0 place-items-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox="0 0 64 64" className="-rotate-90" aria-hidden="true">
        <circle cx="32" cy="32" r={r} fill="none" stroke="var(--muted)" strokeWidth="5" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
        />
      </svg>
      <span
        className="absolute font-mono font-semibold tabular-nums"
        style={{ fontSize: Math.round(size * 0.31), color }}
      >
        {score == null ? "—" : Math.round(score)}
      </span>
    </span>
  );
}

type PillTone = "default" | "success" | "failed" | "ambig";

const PILL_TONES: Record<PillTone, string> = {
  default: "bg-muted text-muted-foreground",
  success: "bg-score-high-soft text-score-high",
  failed: "bg-score-low-soft text-score-low",
  ambig: "bg-score-med-soft text-score-med",
};

/** Mono status/label pill. */
export function Pill({
  tone = "default",
  children,
  className,
}: {
  tone?: PillTone;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[11px] tracking-[0.02em]",
        PILL_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const STATUS_TONE: Record<string, PillTone> = {
  success: "success",
  failed: "failed",
  ambiguous: "ambig",
  pending: "default",
};

export function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? "default";
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <Pill tone={tone}>{label}</Pill>;
}
